import { Component, type ErrorInfo, type ReactNode } from "react";
import { BookOpen } from "lucide-react";
import { reportError } from "../lib/monitoring";

export class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Route React render crashes to Sentry with the component stack attached, so
    // a white-screen for the user becomes an actionable, alerted issue for us.
    reportError(error, { componentStack: info.componentStack, boundary: "root" });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-dvh bg-background flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <BookOpen size={36} className="text-accent/40 mx-auto mb-4" />
            <h1
              className="text-primary mb-2"
              style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "1.4rem", fontWeight: 400 }}
            >
              Something went wrong
            </h1>
            <p className="text-muted-foreground" style={{ fontSize: "0.9rem", lineHeight: "1.65" }}>
              We hit an unexpected bug and our team has been notified. Try reloading the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-5 inline-flex items-center rounded-full bg-accent px-5 py-2 text-accent-foreground"
              style={{ fontSize: "0.85rem" }}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
